import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { FiShoppingBag, FiBarChart2, FiAlertCircle, FiDollarSign, FiPackage } from 'react-icons/fi';
import ErrorBoundary from '../components/ErrorBoundary';
import FOPaymentAnalytics from '../components/FOPaymentAnalytics';

const FODashboardContent = () => {
    const [stats, setStats] = useState({
        totalOrders: 0,
        pendingDOReceived: 0,
        pendingWarehouseRcv: 0,
        pendingShipping: 0,
        pendingCustomerRcv: 0,
        pendingBilled: 0,
        totalProfit: 0,
        totalProfitSGD: 0,
        totalProfitCrypto: 0,
        totalSales: 0,
        averageMargin: 0,
        totalDeliveryFees: 0,
        totalDiscount: 0,
        totalCostRMB: 0,
        totalShipCostRMB: 0,
        totalCostSGD: 0,
        averageDuration: 0,
        lastMonth: {
            monthYear: '',
            totalNewCustomer: 0,
            totalRevenue: 0,
            totalProfit: 0
        },
        thisMonth: {
            monthYear: '',
            totalNewCustomer: 0,
            totalRevenue: 0,
            totalProfit: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sgdRmbRate, setSgdRmbRate] = useState(null);
    const [widgets, setWidgets] = useState({
        top10CustomersByRevenue: [],
        top5NewCustomerByMonth: [],
        top5SubcategoryByOrderItems: [],
        top5SupplierByCost: [],
        top5RecurringCustomerByOrders: []
    });
    const [widgetsLoading, setWidgetsLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foorders/dashboard-stats`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );

                if (response.data.success) {
                    setStats(response.data.data);
                }
            } catch (err) {
                setError(`Failed to fetch statistics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    useEffect(() => {
        const fetchWidgets = async () => {
            try {
                setWidgetsLoading(true);
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foorders/dashboard-widgets`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setWidgets(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch dashboard widgets:', err);
            } finally {
                setWidgetsLoading(false);
            }
        };
        fetchWidgets();
    }, []);

    // Fetch SGD/RMB forex rate on mount (page load/refresh) and refetch every 24 hours
    useEffect(() => {
        const fetchForexRate = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/forex-rates/SGDRMB`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );

                if (response.data.success && response.data.data) {
                    setSgdRmbRate(response.data.data.rate);
                }
            } catch (err) {
                console.error('Failed to fetch SGD/RMB rate:', err);
                // Don't set error state, just log it
            }
        };

        fetchForexRate();
        const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
        const interval = setInterval(fetchForexRate, TWENTY_FOUR_HOURS_MS);

        return () => clearInterval(interval);
    }, []);

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(amount);
    };

    const formatRMB = (amount) => {
        return `RMB ${amount.toFixed(2)}`;
    };

    const formatMargin = (margin) => {
        return `${margin.toFixed(2)}%`;
    };

    const formatDuration = (days) => {
        return `${days.toFixed(1)} days`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full max-w-none px-8">
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">GZ Dashboard Overview</h2>
                    {sgdRmbRate !== null && (
                        <div className="text-sm">
                            <span className="text-gray-700">Current Rate for SGD/RMB: </span>
                            <span className="text-red-600 font-bold">{sgdRmbRate.toFixed(4)}</span>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                        <FiAlertCircle className="mr-2" />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Card 1: Total Orders */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiShoppingBag className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-blue-800">Total Orders</h3>
                                <p className="text-3xl font-bold text-blue-600">{stats.totalOrders}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Pending DO Received</span>
                                <span className="font-medium text-blue-800">{stats.pendingDOReceived ?? 0}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Pending Warehouse Received</span>
                                <span className="font-medium text-blue-800">{stats.pendingWarehouseRcv}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Pending Shipping</span>
                                <span className="font-medium text-blue-800">{stats.pendingShipping}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Pending Customer Received</span>
                                <span className="font-medium text-blue-800">{stats.pendingCustomerRcv}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Pending Billed</span>
                                <span className="font-medium text-blue-800">{stats.pendingBilled}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Profit Analytics */}
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-green-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-green-800">Profit Analytics</h3>
                                <p className="text-3xl font-bold text-green-600">{formatAmount(stats.totalProfit)}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-green-700">Total SGD</span>
                                <span className="font-medium text-green-800">{formatAmount(stats.totalProfitSGD ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-green-700">Total Crypto</span>
                                <span className="font-medium text-green-800">{formatAmount(stats.totalProfitCrypto ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-green-700">Total Revenue</span>
                                <span className="font-medium text-green-800">{formatAmount(stats.totalSales ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-green-700">Average Margin</span>
                                <span className="font-medium text-green-800">{formatMargin(stats.averageMargin ?? 0)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card 3: Order Analytics */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiPackage className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-purple-800">Order Analytics</h3>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Average Duration</span>
                                <span className="font-medium text-purple-800">{formatDuration(stats.averageDuration ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Total Delivery Fees</span>
                                <span className="font-medium text-purple-800">{formatAmount(stats.totalDeliveryFees ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Total Discount</span>
                                <span className="font-medium text-purple-800">{formatAmount(stats.totalDiscount ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Total Cost (RMB)</span>
                                <span className="font-medium text-purple-800">{formatRMB(stats.totalCostRMB ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Total Shipping Cost</span>
                                <span className="font-medium text-purple-800">{formatRMB(stats.totalShipCostRMB ?? 0)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card 4: Last Month + This Month Analytics (stacked, smaller font, same height as other cards) */}
                    <div className="p-4 rounded-lg border border-amber-200 bg-gradient-to-b from-amber-50 to-yellow-50 flex flex-col">
                        <div className="flex items-center mb-2">
                            <FiDollarSign className="h-6 w-6 text-amber-600 flex-shrink-0" />
                            <span className="ml-2 text-sm font-semibold text-amber-900">Month Analytics</span>
                        </div>
                        <div className="flex-1 flex flex-col gap-3 text-xs min-h-0">
                            {/* Last Month */}
                            <div>
                                <p className="font-semibold text-amber-800 mb-1">{stats.lastMonth?.monthYear ?? 'Last month'}</p>
                                <div className="space-y-0.5 text-amber-700">
                                    <div className="flex justify-between"><span>Total New Customer</span><span className="font-medium text-amber-800">{stats.lastMonth?.totalNewCustomer ?? 0}</span></div>
                                    <div className="flex justify-between"><span>Total Order</span><span className="font-medium text-amber-800">{stats.lastMonth?.totalOrder ?? 0}</span></div>
                                    <div className="flex justify-between"><span>Total Revenue</span><span className="font-medium text-amber-800">{formatAmount(stats.lastMonth?.totalRevenue ?? 0)}</span></div>
                                    <div className="flex justify-between"><span>Total Profit</span><span className="font-medium text-amber-800">{formatAmount(stats.lastMonth?.totalProfit ?? 0)}</span></div>
                                </div>
                            </div>
                            <div className="border-t border-amber-200 my-0.5" />
                            {/* This Month */}
                            <div>
                                <p className="font-semibold text-yellow-800 mb-1">{stats.thisMonth.monthYear}</p>
                                <div className="space-y-0.5 text-yellow-700">
                                    <div className="flex justify-between"><span>Total New Customer</span><span className="font-medium text-yellow-800">{stats.thisMonth.totalNewCustomer}</span></div>
                                    <div className="flex justify-between"><span>Total Order</span><span className="font-medium text-yellow-800">{stats.thisMonth.totalOrder ?? 0}</span></div>
                                    <div className="flex justify-between"><span>Total Revenue</span><span className="font-medium text-yellow-800">{formatAmount(stats.thisMonth.totalRevenue)}</span></div>
                                    <div className="flex justify-between"><span>Total Profit</span><span className="font-medium text-yellow-800">{formatAmount(stats.thisMonth.totalProfit)}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FO Payment Analytics Section */}
            <FOPaymentAnalytics />

            {/* Widgets grid: Col1 R1&2 = Top 10 (wider); Col2-4 R1 = Supplier; Col2 R2 = Subcategory; Col3 R2 = New Customer; Col4 R2 = Recurring */}
            <div
                className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr_1fr] gap-6"
                style={{ gridTemplateRows: '1fr 1fr', minHeight: '480px' }}
            >
                {/* Column 1 Rows 1&2: Top 10 Customer By Revenue */}
                <div className="lg:col-start-1 lg:row-start-1 lg:row-end-3 bg-blue-50 rounded-lg shadow p-6 flex flex-col min-h-0">
                    <h3 className="text-base font-semibold mb-4 text-blue-900">Top 10 Customer By Revenue</h3>
                    {widgetsLoading ? (
                        <div className="flex justify-center py-8 flex-1">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto flex-1 min-h-0">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-blue-200 text-left text-blue-800 text-xs font-medium">
                                        <th className="py-2 pr-4">Customer</th>
                                        <th className="py-2 pr-4 text-right">Total Order</th>
                                        <th className="py-2 pr-4 text-right">Total Profit</th>
                                        <th className="py-2 text-right">Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {widgets.top10CustomersByRevenue.length === 0 ? (
                                        <tr><td colSpan={4} className="py-4 text-blue-700">No data</td></tr>
                                    ) : (
                                        widgets.top10CustomersByRevenue.map((row, i) => (
                                            <tr key={i} className="border-b border-blue-100">
                                                <td className="py-2 pr-4">{row.customerID} - {row.name}</td>
                                                <td className="py-2 pr-4 text-right">{row.orderCount ?? 0}</td>
                                                <td className="py-2 pr-4 text-right text-green-600 font-medium">{formatAmount(row.totalProfit)}</td>
                                                <td className="py-2 text-right">{formatAmount(row.revenue)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Column 2&3&4 Row 1: Top 5 Supplier By Cost */}
                <div className="lg:col-start-2 lg:col-end-5 lg:row-start-1 bg-amber-50 rounded-lg shadow p-6 flex flex-col min-h-0">
                    <h3 className="text-base font-semibold mb-4 text-amber-900">Top 5 Supplier By Cost</h3>
                    {widgetsLoading ? (
                        <div className="flex justify-center py-8 flex-1">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto flex-1 min-h-0">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-amber-200 text-left text-amber-800 text-xs font-medium">
                                        <th className="py-2 pr-4">Supplier</th>
                                        <th className="py-2 pr-4 text-right">Items</th>
                                        <th className="py-2 text-right">Total Cost (SGD)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {widgets.top5SupplierByCost.length === 0 ? (
                                        <tr><td colSpan={3} className="py-4 text-amber-700">No data</td></tr>
                                    ) : (
                                        widgets.top5SupplierByCost.map((row, i) => (
                                            <tr key={i} className="border-b border-amber-100">
                                                <td className="py-2 pr-4">{row.supplierLabel}</td>
                                                <td className="py-2 pr-4 text-right">{row.itemCount}</td>
                                                <td className="py-2 text-right">{formatAmount(row.totalCostInSGD)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Column 2 Row 2: Top 5 Subcategory By Order Items */}
                <div className="lg:col-start-2 lg:row-start-2 bg-violet-50 rounded-lg shadow p-6 flex flex-col min-h-0">
                    <h3 className="text-base font-semibold mb-4 text-violet-900">Top 5 Subcategory By Order Items</h3>
                    {widgetsLoading ? (
                        <div className="flex justify-center py-8 flex-1">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto flex-1 min-h-0">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-violet-200 text-left text-violet-800 text-xs font-medium">
                                        <th className="py-2 pr-4">Subcategory</th>
                                        <th className="py-2 text-right">Items Ordered</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {widgets.top5SubcategoryByOrderItems.length === 0 ? (
                                        <tr><td colSpan={2} className="py-4 text-violet-700">No data</td></tr>
                                    ) : (
                                        widgets.top5SubcategoryByOrderItems.map((row, i) => (
                                            <tr key={i} className="border-b border-violet-100">
                                                <td className="py-2 pr-4">{row.subcategory}</td>
                                                <td className="py-2 text-right">{row.itemCount}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Column 3 Row 2: Top 5 New Customer By Month */}
                <div className="lg:col-start-3 lg:row-start-2 bg-emerald-50 rounded-lg shadow p-6 flex flex-col min-h-0">
                    <h3 className="text-base font-semibold mb-4 text-emerald-900">Top 5 New Customer By Month</h3>
                    {widgetsLoading ? (
                        <div className="flex justify-center py-8 flex-1">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto flex-1 min-h-0">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-emerald-200 text-left text-emerald-800 text-xs font-medium">
                                        <th className="py-2 pr-4">Month Year</th>
                                        <th className="py-2 text-right">Total Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {widgets.top5NewCustomerByMonth.length === 0 ? (
                                        <tr><td colSpan={2} className="py-4 text-emerald-700">No data</td></tr>
                                    ) : (
                                        widgets.top5NewCustomerByMonth.map((row, i) => (
                                            <tr key={i} className="border-b border-emerald-100">
                                                <td className="py-2 pr-4">{row.monthYear}</td>
                                                <td className="py-2 text-right">{row.count}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Column 4 Row 2: Top 5 Recurring Customer By Orders */}
                <div className="lg:col-start-4 lg:row-start-2 bg-teal-50 rounded-lg shadow p-6 flex flex-col min-h-0">
                    <h3 className="text-base font-semibold mb-4 text-teal-900">Top 5 Recurring Customer By Orders</h3>
                    {widgetsLoading ? (
                        <div className="flex justify-center py-8 flex-1">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto flex-1 min-h-0">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-teal-200 text-left text-teal-800 text-xs font-medium">
                                        <th className="py-2 pr-4">Customer</th>
                                        <th className="py-2 text-right">Order Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {widgets.top5RecurringCustomerByOrders.length === 0 ? (
                                        <tr><td colSpan={2} className="py-4 text-teal-700">No data</td></tr>
                                    ) : (
                                        widgets.top5RecurringCustomerByOrders.map((row, i) => (
                                            <tr key={i} className="border-b border-teal-100">
                                                <td className="py-2 pr-4">{row.customerID} - {row.name}</td>
                                                <td className="py-2 text-right">{row.orderCount}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Spacer so table section is not flush with bottom of page */}
            <div className="min-h-24" aria-hidden="true" />
        </div>
    );
};

const FODashboard = () => {
    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="GZ Dashboard Error"
                fallbackDescription="An error occurred while loading the GZ dashboard. Please try again or contact support if the problem persists."
            >
                <FODashboardContent />
            </ErrorBoundary>
        </Layout>
    );
};

export default FODashboard;
